/**
 * QA Broadcast Overrides
 * Provides functionality for QA team to override broadcast hosts for testing
 */

const QA_BROADCAST_OVERRIDES_KEY = "qa_broadcast_overrides";

export interface BroadcastHostOverrides {
  rtmpHost?: string;
  rtmpVrHost?: string;
  wsHost?: string;
}

/**
 * Get QA broadcast overrides from localStorage
 */
export const getQABroadcastOverrides = (): BroadcastHostOverrides => {
  try {
    const stored = localStorage.getItem(QA_BROADCAST_OVERRIDES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Failed to load QA broadcast overrides:", error);
    return {};
  }
};

/**
 * Save QA broadcast overrides to localStorage
 */
export const setQABroadcastOverrides = (
  overrides: BroadcastHostOverrides
): void => {
  try {
    localStorage.setItem(QA_BROADCAST_OVERRIDES_KEY, JSON.stringify(overrides));
  } catch (error) {
    console.error("Failed to save QA broadcast overrides:", error);
  }
};

/**
 * Clear all QA broadcast overrides
 */
export const clearQABroadcastOverrides = (): void => {
  try {
    localStorage.removeItem(QA_BROADCAST_OVERRIDES_KEY);
  } catch (error) {
    console.error("Failed to clear QA broadcast overrides:", error);
  }
};

/**
 * Replace the host in a URL while preserving protocol and port
 * @param url - Original URL (e.g., "rtmp://origins.streamray.com:1935")
 * @param newHost - New host to use (e.g., "test-origins.streamray.com")
 * @returns Modified URL with new host
 */
export const replaceUrlHost = (url: string, newHost: string | undefined): string => {
  if (!newHost || !url) {
    return url;
  }

  try {
    // Parse the URL to extract protocol, host, and port
    const urlPattern = /^(\w+:\/\/)([^:/]+)(:\d+)?(.*)$/;
    const match = url.match(urlPattern);

    if (!match) {
      console.warn(`Invalid URL format: ${url}`);
      return url;
    }

    const [, protocol, , port, path] = match;

    // Reconstruct URL with new host but original protocol and port
    return `${protocol}${newHost}${port || ""}${path || ""}`;
  } catch (error) {
    console.error("Failed to replace URL host:", error);
    return url;
  }
};

/**
 * Apply QA overrides to broadcast URLs
 */
export const applyQABroadcastOverrides = (
  rtmpUrl: string,
  rtmpVrUrl: string,
  wsUrl: string
): {
  broadcastRtmp: string;
  broadcastRtmpVr: string;
  broadcastWs: string;
} => {
  const overrides = getQABroadcastOverrides();

  return {
    broadcastRtmp: replaceUrlHost(rtmpUrl, overrides.rtmpHost),
    broadcastRtmpVr: replaceUrlHost(rtmpVrUrl, overrides.rtmpVrHost),
    broadcastWs: replaceUrlHost(wsUrl, overrides.wsHost),
  };
};
